import { ClassKey } from '@const/class.constants';
import { MenuResponseDTO } from '@dto/menuResponse.dto';
import { DashboardContextMenu } from '@parts/dashboard/select/DashboardContextMenu';
import { Button, GetRef, Tooltip, Tree } from 'antd';
import { DataNode, EventDataNode } from 'antd/es/tree';
import { AxiosError } from 'axios';
import React, { Key, useEffect, useMemo, useRef, useState } from 'react';
import { FaMinus, FaRegStar } from 'react-icons/fa';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { useAppGlobStore } from 'src/appGlobalStore';
import { XmSpinner } from 'src/component/spinner/XmSpinner';
import { FolderContextMenuBase, MenuDataNode as FolderMenuDataNode } from 'src/content/configuration/FolderContextMenuBase';
import { ContextState, CustomFolderContextMenu } from 'src/content/configuration/cg/CustomFolderContextMenu';
import HistorySearch from 'src/content/search/components/SearchHistory';
import { API_URL } from 'src/data/Api';
import { Const } from 'src/data/Const';
import { useWebSocketReloadGetRandomNumberOnReload } from 'src/hook/useWebSocketReload';
import { MenuNode } from 'src/model/MenuNode';
import { getPathUrl, mergeTree } from 'src/routing/NavigateRoute';
import { DataService } from 'src/service/data.service';
import { MenuService } from 'src/service/menu.service';
import { DomUtil } from 'src/util/DomUtil';
import { getApi } from 'src/util/apiCalls';
import { RoutePath, isDevicesTree } from '../../data/Routes';
import { Log } from '../../service/log.service';
import { GLOB } from '../../util/Glob';
import { GroupNavFolderContextMenu } from './GroupNavFolderContextMenu';
import LeftMenuSearch from './Search/LeftMenuSearch';
import './SideNavBar.less';

const { DirectoryTree } = Tree;

export function mapParent(treeNodes: MenuNode[], parent: MenuNode) {
  if (!treeNodes) return;
  for (const tn of treeNodes) {
    //if (!tn.parent)
    tn.parent = parent;
    mapParent(tn.children, tn);
  }
}

function getGroupParent(node: MenuNode) {
  return MenuNode.getTopParent(node, (node) => !node?.group_id && !node?.isLeaf && (node.isStorageGroup || node.isGroupFolder));
}

export function getHwTypeParent(node: MenuNode): MenuNode {
  const result = getHwTypeParentRekur(node);
  if (result?.hwType) return result;
}
function getHwTypeParentRekur(node: MenuNode): MenuNode {
  if (!node) return;
  const parent = getHwTypeParent(node.parent);
  if (parent?.hwType) return parent;
  return node;
}

let newTree: MenuNode;
function spreadTree(node: MenuNode, tree: MenuNode) {
  if (!node.parent) {
    return (newTree = { ...tree });
  }
  if (node.parent) {
    const parent = spreadTree(node.parent, tree);
    let found: MenuNode = parent;
    if (parent?.children)
      parent.children = parent.children.map((n) => {
        const nod = n.key === node.key ? (found = { ...n }) : n;
        return nod;
      });
    return found;
  }
}

function makeLinkPath(node: MenuNode): string[] {
  if (!node?.parent)
    return [node.path];

  return [...makeLinkPath(node.parent), node.path];
}

let nav: NavigateFunction;
export function reloadPage() {
  const currentLocation = decodeURIComponent(GLOB.location.pathname);
  const search = GLOB.location.search;
  const hash = GLOB.location.hash;
  nav(RoutePath.RELOAD, { replace: true });
  setTimeout(() => {
    nav(currentLocation + search + hash, { replace: true });
  });
}

let prevKey = '';
let selectedFromTree = false;

export const SideNavBar: React.FC = () => {
  const navigate = useNavigate();
  nav = navigate;
  GLOB.location = useLocation();
  const leftMenuSelectedKeys = useAppGlobStore(s => s.leftMenuSelectedKeys);
  const topMenu = useAppGlobStore(s => s.topMenu);
  const leftMenuGlob = useAppGlobStore(s => s.leftMenu);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [loadedKeys, setLoadedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [state, setState] = useState(() => ({ groupReload: false, timeout: 0, selectFirst: false, reloading: false, leftMenu: { ...leftMenuGlob } }));
  const treeRef = useRef<GetRef<typeof DirectoryTree>>();
  const domRef = useRef<HTMLElement>();
  const aborter = useRef<AbortController>();
  const leftMenu = state.leftMenu;
  const dashContextMenu = useMemo(() => new DashboardContextMenu(
    () => ({ options: leftMenu.children }),
    ({ options }) => useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: options } }),
    item => item.isLeaf, null,
    deletedNode => deletedNode.key === GLOB.selectedItem.key && navigate(GLOB.getDefaultPath()),
    reloadPage
  ),
    [leftMenu]);
  const cgContextMenu = useMemo(() => new CustomFolderContextMenu(() => ({ folders: [leftMenu] as FolderMenuDataNode[] }),
    (state: ContextState) => useAppGlobStore.setState({ leftMenu: ({ ...leftMenu, children: [...state.folders[0].children] }) }), null, true), [leftMenu]);
  const reloadFolderTree = (serverRefetch?: boolean) => {
    if (serverRefetch) {
      if (GLOB.selectedClass === RoutePath.STORAGE) {
        void GLOB.menuService.reloadStorageGroups().then(() => {
          const newChildren = GLOB.menuService.storagesBy.group.children;
          state.leftMenu.children = newChildren;
          mapParent(newChildren, state.leftMenu);
          setExpandedKeys([]);
          setLoadedKeys([]);
          useAppGlobStore.setState({ leftMenu: { ...state.leftMenu, children: [...newChildren] } });
        });
      } else {
        reloadTree();
      }
    } else {
      setTimeout(() => setState(s => ({ ...s })));
    }
  };
  const sgFolderContextMenu = useMemo(() => new GroupNavFolderContextMenu(
    () => ({ folders: [leftMenu] as FolderMenuDataNode[] }),
    (state) => useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: [...state.folders[0].children] } }),
    null, { class: 'storage' }, reloadFolderTree
  ), [leftMenu]);
  const serverFolderContextMenu = useMemo(() => new GroupNavFolderContextMenu(
    () => ({ folders: [leftMenu] as FolderMenuDataNode[] }),
    (state) => useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: [...state.folders[0].children] } }),
    null, {}, reloadFolderTree
  ), [leftMenu]);
  const wsReload = useWebSocketReloadGetRandomNumberOnReload('menu');

  useEffect(() => {
    return () => {
      window.clearTimeout(state.timeout);
    };
  });

  useEffect(() => {
    if (wsReload) {
      void GLOB.menuService.init();
      if ([RoutePath.DASHBOARD, RoutePath.SETTINGS, RoutePath.PROFILE].some(rp => rp === GLOB.selectedClass)) return;
      reloadTree();
    }
  }, [wsReload]);

  useEffect(() => {
    aborter.current = new AbortController();
    const interval = setInterval(() => {
      void GLOB.menuService.init();
      if ([RoutePath.DASHBOARD, RoutePath.SETTINGS, RoutePath.PROFILE].some(rp => rp === GLOB.selectedClass)) return;

      const cls = GLOB.selectedClass;
      const aType = GLOB.selectedAllType;
      const type = GLOB.selectedType;
      const itemSearch = GLOB.location.pathname.substring(GLOB.location.pathname.lastIndexOf('/')) + GLOB.location.search;
      const getUrl = getPathUrl(GLOB.selectedClass, GLOB.selectedAllType, GLOB.selectedGroup, GLOB.selectedType, itemSearch);
      getApi<MenuResponseDTO>(getUrl, aborter.current.signal).then(response => {
        if (cls !== GLOB.selectedClass || aType !== GLOB.selectedAllType || type !== GLOB.selectedType && !aType) return;
        const incoming = response.data.data;
        const leftMenu = useAppGlobStore.getState().leftMenu;
        if (leftMenu.children.some(mn => incoming.some(mi => mi.url === mn.url))) {
          mergeTree(leftMenu.children, response.data.data, GLOB.selectedGroup);
          useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: [...leftMenu.children] } });
        } else {
          for (const subMenu of leftMenu.children) {
            if (subMenu.children?.some(mn => incoming.some(mi => mi.url === mn.url))) {
              mergeTree(subMenu.children, incoming, GLOB.selectedGroup);
              useAppGlobStore.setState({ leftMenu: { ...leftMenu, children: [...leftMenu.children] } });
              return;
            }
          }
          Log.debug(`Failed to update left tree menu ${location.href} with ${getUrl}!`);
        }
      }, (reason: AxiosError) => Log.error('Failed to refresh current tree', reason));
    }, 1000 * 60 * 60);
    return () => {
      clearInterval(interval);
      aborter.current.abort();
    };
  }, []);

  useEffect(() => {
    GLOB.scrollSelectedItemIntoView = () => {
      setExpandedKeys(prev => [...prev, ...MenuNode.getParentKeys(GLOB.selectedItem?.isLeaf ? GLOB.selectedItem?.parent : GLOB.selectedItem)].filter(GLOB.uniqueArrayFilter));
      setTimeout(() => {
        DomUtil.waitForElm('.ant-tree-treenode-selected', domRef.current).then(el => {
          setTimeout(() => {
            domRef.current?.querySelector('.ant-tree-treenode-selected')?.scrollIntoView({ block: 'center' });
            // doesn't work: treeRef.current.scrollTo({ key: GLOB.selectedItem.key });
          }, Const.TIMEOUT_DEBOUNCE_SHORT);
        }, (reason: unknown) => reason);
      });
    };
  }, [expandedKeys]);

  useEffect(() => {
    if (selectedFromTree || !leftMenuSelectedKeys?.length) {
      selectedFromTree = false;
    } else
      GLOB.scrollSelectedItemIntoView();
  }, [leftMenuSelectedKeys]);

  useEffect(() => {

    window.clearTimeout(state.timeout);

    const leftMenu = state.leftMenu = { ...leftMenuGlob };

    if (!leftMenu) return;

    if (prevKey !== leftMenu?.key && !leftMenu.isSearchTree) {
      //if (!loadedKeys.every(k => isKeyWithinTree(k, tree)))
      setLoadedKeys([]);
      //if (!expandedKeys.every(k => isKeyWithinTree(k, tree)))
      setExpandedKeys([]);
    }

    if (leftMenu?.url && !leftMenu.children) {
      GLOB.treeLoaded.reset();

      const cls = GLOB.selectedClass;
      const aType = GLOB.selectedAllType;
      const type = GLOB.selectedType;
      getApi<MenuResponseDTO>(leftMenu.url, aborter.current.signal).then(
        (response) => {
          if (cls !== GLOB.selectedClass || aType !== GLOB.selectedAllType || type !== GLOB.selectedType && !aType) return;
          useAppGlobStore.setState((prev) => {
            if (prev.leftMenu) spreadTree(prev.leftMenu, prev.leftMenu);
            else newTree = leftMenu;
            newTree.children = response.data.data.map((mi) => MenuNode.menuItemToNode(mi));
            state.leftMenu = { ...newTree };
            return { leftMenu: newTree };
          });
          if (!GLOB.selectedItem) {
            setTimeout(() => {
              navigate(GLOB.selectedClass + GLOB.selectedType + GLOB.getItemPath(newTree.children[0]), { replace: true });
            });
          }
          GLOB.treeLoaded.resolve();
        },
        (reason: AxiosError) => {
          Log.error('Failed to get tree!', reason);
          GLOB.treeLoaded.reject();
        }
      );
    } else {
      GLOB.treeLoaded.resolve();
    }
    GLOB.treeLoaded.promise.then(() => {
      const leftMenu = state.leftMenu;
      mapParent(leftMenu?.children, leftMenu);

      if (GLOB.selectedItem)
        if (prevKey !== leftMenu?.key) {
          if (!GLOB.selectedItem.isLeaf && !GLOB.selectedItem.children)
            setLoadedKeys(expandedKeys.filter(k => k !== GLOB.selectedItem.key));
          else
            setLoadedKeys([...expandedKeys]);
          GLOB.scrollSelectedItemIntoView();
        } else if (!treeRef.current.props.selectedKeys.some(k => k === GLOB.selectedItem.key)) {
          GLOB.scrollSelectedItemIntoView();
        }

      prevKey = leftMenu?.key as string;

    }, (reason: AxiosError) => console.error('Tree not loaded', reason));
  }, [leftMenuGlob]);

  function isWithinTree(node: MenuNode) {
    if (node === leftMenu || node?.key === leftMenu?.key) return true;
    if (node.parent) return isWithinTree(node.parent);
    return false;
  }

  function reloadLeftMenu() {
    state.reloading = true;
    GLOB.selectedAllType = GLOB.selectedClass = GLOB.selectedGroup = GLOB.selectedType = GLOB.selectedItem = null;
    setLoadedKeys([]);
    setExpandedKeys([]);
    mapParent(leftMenu?.children, leftMenu);
    setTimeout(() => {
      reloadPage();
      state.reloading = false;
    });
  }
  DataService.reloadLeftMenu = reloadLeftMenu;

  function reloadTree() {
    const itemSearch = GLOB.location.pathname.substring(GLOB.location.pathname.lastIndexOf('/')) + GLOB.location.search;
    const isStorage = GLOB.selectedClass === "/storage";
    const folderPath = isStorage ? "/folder/class" + GLOB.selectedClass : "/folder?class=" + GLOB.selectedClass.slice(1);
    const url = GLOB.selectedType ? getPathUrl(GLOB.selectedClass, GLOB.selectedAllType, GLOB.selectedGroup, GLOB.selectedType, itemSearch)
      : API_URL.MENU + folderPath;
    const cls = GLOB.selectedClass;
    const aType = GLOB.selectedAllType;
    const type = GLOB.selectedType;
    getApi<MenuResponseDTO>(url, aborter.current.signal).then(response => {
      if (cls !== GLOB.selectedClass || aType !== GLOB.selectedAllType || type !== GLOB.selectedType && !aType) return;
      if (state.leftMenu.children.some(mn => response.data.data.some(mi => mi.url === mn.url))) {
        state.leftMenu.children = response.data.data.map(mi => MenuNode.menuItemToNode(mi, GLOB.selectedGroup));
        setExpandedKeys([]);
        setLoadedKeys([]);
        useAppGlobStore.setState(({ leftMenu: { ...state.leftMenu, children: [...state.leftMenu.children] } }));
      } else {
        const keysToStay = state.leftMenu.children.map(sm => sm.url);
        setLoadedKeys(prev => prev.filter(k => keysToStay.some(ks => k === ks)));
        setExpandedKeys(prev => prev.filter(k => keysToStay.some(ks => k === ks)));
        for (const subMenu of state.leftMenu.children) {
          if (subMenu.children?.some(mn => response.data.data.some(mi => mi.url === mn.url))) {
            subMenu.children = response.data.data.map(mi => MenuNode.menuItemToNode(mi, GLOB.selectedGroup));
            break;
          }
        }
        useAppGlobStore.setState(({ leftMenu: { ...state.leftMenu, children: [...state.leftMenu.children] } }));
      }
      state.groupReload = true;
    }, (reason: AxiosError) => Log.error('Failed to refresh current tree', reason));
  }

  function onLoadData(node: MenuNode) {
    if (state.reloading) return Promise.resolve();
    if (state.groupReload) {
      state.groupReload = false;
      return Promise.resolve();
    }
    if (!isWithinTree(node)) {
      window.clearTimeout(state.timeout);
      state.timeout = window.setTimeout(() => {
        Log.warn(`Node ${node.key} not part of tree ${leftMenu?.key}!`);
      }, Const.TIMEOUT_DEBOUNCE_SHORT);
      setExpandedKeys(prev => prev.filter(k => k !== node.key));
      return Promise.reject(new Error(`Node ${node.key} is not part of the tree ${leftMenu?.key}!`));
    }
    return new Promise<void>((resolve, reject) => {
      if (node.children && (node.children.length || !node.url)) {
        spreadTree(leftMenu, leftMenu);
        useAppGlobStore.setState({ leftMenu: newTree });
        setLoadedKeys(prev => [...prev, node.key as string]);
        setExpandedKeys(prev => [...prev, node.key as string]);
        resolve();

        return;
      }
      GLOB.treeLoaded.reset();
      if (node.url) {
        const cls = GLOB.selectedClass;
        const aType = GLOB.selectedAllType;
        const type = GLOB.selectedType;
        getApi<MenuResponseDTO>(node.url, aborter.current.signal).then(
          (response) => {
            if (cls !== GLOB.selectedClass || aType !== GLOB.selectedAllType || type !== GLOB.selectedType && !aType) return;
            useAppGlobStore.setState((prev) => {
              const expanded = spreadTree(node, prev.leftMenu);
              const group = GLOB.selectedAllType === RoutePath.GROUP ? MenuNode.getGroupPathSegment(getGroupParent(node)) : '';
              expanded.children = response.data.data.map((mi) => MenuNode.menuItemToNode(mi, group));
              if (GLOB.selectedClass === RoutePath.CUSTOM_GROUPS || GLOB.selectedClass === RoutePath.DASHBOARD)
                expanded.children.sort(MenuService.getFolderSorter(false));
              mapParent(expanded.children, expanded);
              resolve();
              setTimeout(() => {
                selectFirst(expanded.children);
                setLoadedKeys(prev => [...prev, node.key as string]);
                setExpandedKeys(prev => [...prev, node.key as string]);
              });
              const menu = topMenu;
              const root = menu.find(mn => mn.path === GLOB.selectedClass);
              let menuTree = root;
              if (GLOB.selectedType && !GLOB.selectedAllType) {
                menuTree = root.children.find(mn => mn.path === GLOB.selectedType);
              }
              if (menuTree) {
                menuTree.children = newTree.children;
                setTimeout(() => {
                  useAppGlobStore.setState({ topMenu: [...menu] });
                });
              }
              return { leftMenu: newTree };
            });
          },
          (reason: AxiosError) => {
            Log.error('Failed to get sub-menu!', reason);
            GLOB.treeLoaded.reject();
            reject(new Error('Failed to get sub-menu'));
            setLoadedKeys(prev => [...prev, node.key as string]);
          }
        );
      }
    });
  }

  const handleCollapse = () => {
    setExpandedKeys([]);
    if (domRef.current) domRef.current.scrollTo({ top: 0 });
  };

  const onExpand = (
    keys: Key[],
    info: {
      node: EventDataNode<DataNode>;
      expanded: boolean;
      nativeEvent: MouseEvent;
    }
  ) => {
    setExpandedKeys((info.expanded ? keys : keys.filter(k => k !== info.node.key)) as string[]);
  };

  const onSelect = (
    keys: React.Key[],
    info: {
      event: 'select';
      selected: boolean;
      node: EventDataNode<DataNode>;
      selectedNodes: MenuNode[];
      nativeEvent: MouseEvent;
    }
  ) => {
    const selected = info.selectedNodes[0];
    if (!selected) {
      console.error('left tree select fail', keys, info, leftMenu, GLOB.location);
      Log.error('Failed to find selected key in left tree!', { keys, info, loc: GLOB.location, leftMenu, });
      return;
    }

    if (selected.isLeaf) {
      useAppGlobStore.setState({ isMobileLeftTreeOpen: false });
    }

    const pathNew = getPathFormMenuNode(selected, topMenu);
    if (pathNew) {
      const pathCurrent = decodeURIComponent(GLOB.location.pathname + GLOB.location.search);
      selectedFromTree = true;
      if (pathNew === pathCurrent) {
        reloadPage();
      } else {
        navigate(pathNew + GLOB.location.hash);
      }
    }
  };

  const titleRender = (node: MenuNode) => {
    let title = node.title;
    if (GLOB.selectedClass === RoutePath.DASHBOARD && !MenuNode.isDashExcluded(node)) {
      if (!node.titleText && !node.divider)
        node.titleText = node.title as string;

      title = dashContextMenu.createLabel(node);
    } else if (GLOB.selectedClass === RoutePath.CUSTOM_GROUPS) {
      if (!node.titleText)
        node.titleText = node.title as string;

      title = cgContextMenu.createLabel(node as FolderMenuDataNode);
    } else if (!node.isLeaf && !node.isStorageGroup && (
      node.isGroupFolder || (node.isPersonalGroupOrFolder && node.isGroupFolder)
      || (GLOB.selectedClass === RoutePath.STORAGE && GLOB.selectedAllType === RoutePath.GROUP)
      || GLOB.selectedClass === RoutePath.SERVER_GROUP
    )) {
      if (!node.titleText)
        node.titleText = node.title as string;
      const menu = GLOB.selectedClass === RoutePath.STORAGE ? sgFolderContextMenu : serverFolderContextMenu;
      title = menu.createLabel(node as FolderMenuDataNode);
    } else {
      title = node.titleText ?? title;
    }


    if (node.styleClass === 'bold')
      title = <strong>{title}</strong>;
    else if (node.styleClass === 'star')
      title = <div style={{ display: 'inline-flex' }}>{title}<FaRegStar style={{ marginLeft: '0.25em' }} title='Default dashboard' /></div>;



    const handleOpenInNewTab = (e: React.MouseEvent) => {
      if (e.ctrlKey || e.metaKey || e.button === 1) {
        e.stopPropagation();
        window.open(getPathFormMenuNode(node, topMenu), '_blank');
      }
    };

    return <span onClick={handleOpenInNewTab} onAuxClick={handleOpenInNewTab}>{title}</span>;
  };

  function getTreeTitle() {
    if (GLOB.selectedClass === RoutePath.DASHBOARD)
      return dashContextMenu.createLabel({ key: DashboardContextMenu.ROOT, itemId: DashboardContextMenu.ROOT, titleText: leftMenu?.title as string });

    if (GLOB.selectedClass === RoutePath.CUSTOM_GROUPS) {
      leftMenu && (leftMenu.itemId = CustomFolderContextMenu.ROOT);
      //CustomFolderContextMenu.selectRef = null;
      return cgContextMenu.createLabel({ key: CustomFolderContextMenu.ROOT, itemId: CustomFolderContextMenu.ROOT, titleText: leftMenu?.title as string });
    }

    if (GLOB.selectedClass === RoutePath.STORAGE && GLOB.selectedAllType === RoutePath.GROUP) {
      leftMenu && (leftMenu.itemId = FolderContextMenuBase.ROOT);
      return sgFolderContextMenu.createLabel({ key: FolderContextMenuBase.ROOT, itemId: FolderContextMenuBase.ROOT, titleText: leftMenu?.title as string });
    }

    if (GLOB.selectedClass === RoutePath.SERVER_GROUP) {
      leftMenu && (leftMenu.itemId = FolderContextMenuBase.ROOT);
      return serverFolderContextMenu.createLabel({ key: FolderContextMenuBase.ROOT, itemId: FolderContextMenuBase.ROOT, titleText: leftMenu?.title as string });
    }

    return leftMenu?.titleText ?? leftMenu?.title;
  }

  function selectFirst(children: MenuNode[]) {
    if (state.selectFirst) {
      state.selectFirst = false;
      const toSelect = MenuNode.selectFirst(children);
      if (toSelect && GLOB.selectedItem !== toSelect) onSelect(null, { selectedNodes: [toSelect], event: 'select', nativeEvent: null, node: null, selected: null });
    }
  }

  const treeTitle = getTreeTitle();
  const isSearch = treeTitle === "Search";
  const searchHistoryLimit = GLOB?.userInfo?.configuration?.searchHistoryLimit;

  return (
    //<ConfigProvider theme={{ // all token values are ignored:(

    <>
      <nav className={`left-tree ${isSearch ? "left-tree-height" : ""}`} ref={domRef}>
        <h3>{treeTitle}</h3>
        {isLoadingSearch &&
          <div className='search-spinner-overlay'>
            <XmSpinner className='search-spinner' />
          </div>
        }
        <DirectoryTree<MenuNode>
          style={{ height: "35px" }}
          ref={treeRef}
          blockNode={true}
          defaultExpandParent={true}
          expandedKeys={expandedKeys}
          loadData={onLoadData}
          loadedKeys={loadedKeys}
          onExpand={onExpand}
          onSelect={onSelect}
          selectedKeys={leftMenuSelectedKeys}
          showIcon={false}
          showLine={false}
          titleRender={titleRender}
          treeData={leftMenu?.children?.length ? leftMenu.children : []}
          onClick={(e, node) => {
            if (node.isLeaf || node.expanded) return;
            state.selectFirst = true;
            if (node.children?.length > 0) {
              selectFirst(node.children);
            }
          }}
        />
        {isSearch && searchHistoryLimit && (
          <HistorySearch historyState={searchHistoryLimit} userId={GLOB.userInfo.user_id} />
        )}
        {/* <div className='license-footer'>
          <a href='https://xorux.com/' target="_blank">XORUX</a> apps&nbsp;family {appGlobals.info?.backend.free &&
            <a href='https://xormon.com/support.php' target="_blank">Free&nbsp;Edition</a>}
        </div> */}
      </nav>
      {GLOB.selectedClass && ClassKey[GLOB.selectedClass.slice(1).toUpperCase()] && GLOB.selectedAllType !== "/group" &&
        <div className='search-wrapper' >
          <Tooltip title="Collapse left menu" placement="right">
            <Button className='search-collapse-button' size='small' icon={<FaMinus style={{}} />} type="primary" onClick={handleCollapse} />
          </Tooltip>
          <LeftMenuSearch onSelect={onSelect} setIsLoading={setIsLoadingSearch} setLoadedKeys={setLoadedKeys} setExtendednKeys={setExpandedKeys} reloadTree={reloadTree} />
        </div>
      }
    </>
  );
};

function getPathFormMenuNode(node: MenuNode, topMenu: MenuNode[]) {
  let pathNew: string[];
  if (node.path && node.isLeaf) {
    pathNew = [GLOB.selectedClass, GLOB.selectedAllType, GLOB.selectedType, node.path];
    if (MenuNode.getTopParent(node)?.title === "Logs")
      pathNew = [GLOB.selectedClass, ...makeLinkPath(node)];
  } else if (node.url && node.isLeaf) {
    if (GLOB.selectedType && !([RoutePath.LAN_CISCO, RoutePath.LAN_OTHERS, RoutePath.LAN_FIREWALL_CISCO] as string[]).includes(GLOB.selectedType)) {
      const cls = topMenu.find(mn => mn.path === GLOB.selectedClass);
      const qIdx = node.url.indexOf('?');
      const urlNoQ = qIdx >= 0 ? node.url.substring(0, qIdx) : node.url;
      const itemIdx = urlNoQ.lastIndexOf('/');
      const item = node.url.substring(itemIdx);
      const typeIdx = urlNoQ.lastIndexOf('/', itemIdx - 1);
      const typePath = node.url.substring(typeIdx, itemIdx);
      const type = typePath.substring(1);

      if (isDevicesTree(GLOB.selectedAllType)) {
        let groupPath = '';
        if (GLOB.selectedAllType === RoutePath.GROUP) {
          const top = getGroupParent(node);
          if (!top.isLeaf)
            groupPath = '/' + top.itemId;
        }
        const device = getHwTypeParent(node);
        pathNew = [GLOB.selectedClass, GLOB.selectedAllType, groupPath, device ? ('/' + device.titleText) : '', item];
      } else if (cls.children.some(mn => mn.path === typePath || mn.hwType === type)) {
        pathNew = [GLOB.selectedClass, GLOB.selectedAllType, typePath, item];
      } else if (cls.path === typePath) {
        const typeSegment = cls.path === RoutePath.TAPE ? RoutePath.TYPE : GLOB.selectedAllType;
        pathNew = [GLOB.selectedClass, typeSegment, item];
      } else {
        const clsOther = topMenu.find(mn => mn.children.some(c => c.path === typePath));
        if (clsOther) {
          pathNew = [clsOther.path, typePath, item];
        } else {
          //multiple hw_types under fabric
          const nstdClsHwType = topMenu.find(mn => mn?.children?.some(c => c?.children?.some(cc => cc.hwType === type)));
          pathNew = [nstdClsHwType.path, GLOB.selectedAllType, typePath, item];
        }
      }

    } else {
      let groupPath = '';
      if (GLOB.selectedAllType === RoutePath.GROUP) {
        const top = getGroupParent(node);
        if (!top.isLeaf)
          groupPath = '/' + top.itemId;
      }
      const device = getHwTypeParent(node);
      const deviceTypePath = GLOB.selectedClass === RoutePath.CUSTOM_GROUPS || GLOB.selectedClass === RoutePath.DASHBOARD || !device ? '' :
        ('/' + (isDevicesTree(GLOB.selectedAllType) ? device.titleText : device.hwType));
      pathNew = [GLOB.selectedClass, GLOB.selectedAllType, groupPath, deviceTypePath, GLOB.getItemPath(node)];
    }
  }

  return pathNew?.join('');
}